import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUtensils,
  faPlus,
  faPen,
  faTrash,
  faCheck,
  faExclamationTriangle,
  faBell,
  faSpinner,
  faClock,
  faDroplet,
  faNotesMedical,
  faArrowRight,
  faBaby,
  faBottleWater,
  faAppleWhole,
  faFilter,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import womensHealthService, {
  PostpartumProfile,
  FeedingLog,
} from '../../../services/womensHealthService';
import BackButton from '../../../components/BackButton/BackButton';
import WHArticleList from '../../../components/WHArticleList/WHArticleList';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import ProfileStrip from '../../../components/ProfileStrip/ProfileStrip';
import './Feeding.css';
import { useProfile } from '../../../context/ProfileContext';

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

type TabKey = 'log' | 'history' | 'solids';
type DateFilter = 'today' | 'week' | 'all';

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ChoiceOption {
  value: string;
  display_name: string;
}

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const formatDate = (iso: string | null): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatTime = (iso: string | null): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '--';
  return `${formatDate(iso)} ${formatTime(iso)}`;
};

const nowLocalDatetime = (): string => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

const isToday = (iso: string): boolean => {
  const d = new Date(iso);
  const now = new Date();
  return d.toDateString() === now.toDateString();
};

const isThisWeek = (iso: string): boolean => {
  const d = new Date(iso);
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  return d >= weekAgo && d <= now;
};

/* ================================================================== */
/*  Toast                                                              */
/* ================================================================== */

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const iconMap: Record<string, IconDefinition> = {
    success: faCheck,
    error: faExclamationTriangle,
    info: faBell,
  };

  return (
    <div className={`wh-feed-toast wh-feed-toast-${type}`}>
      <span className="wh-feed-toast-icon">
        <FontAwesomeIcon icon={iconMap[type]} />
      </span>
      <span className="wh-feed-toast-message">{message}</span>
      <button className="wh-feed-toast-close" onClick={onClose} aria-label="Close notification">
        {'\u00D7'}
      </button>
    </div>
  );
};

/* ================================================================== */
/*  Skeleton                                                           */
/* ================================================================== */

const SkeletonCards: React.FC = () => (
  <div className="wh-feed-skeleton-grid">
    {[0, 1, 2].map((i) => (
      <div key={i} className="wh-feed-skeleton-card">
        <div className="wh-feed-skeleton-line long" />
        <div className="wh-feed-skeleton-line medium" />
        <div className="wh-feed-skeleton-line short" />
      </div>
    ))}
  </div>
);

/* ================================================================== */
/*  Initial Form                                                       */
/* ================================================================== */

const INITIAL_FORM = {
  feeding_type: '',
  side: '',
  start_time: nowLocalDatetime(),
  duration_minutes: '' as string | number,
  volume_ml: '' as string | number,
  notes: '',
};

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

const Feeding: React.FC = () => {
  const { t } = useTranslation(['womensHealth', 'common']);
  const { activeProfile } = useProfile();
  const [searchParams] = useSearchParams();

  // Data
  const [profile, setProfile] = useState<PostpartumProfile | null>(null);
  const [feedingLogs, setFeedingLogs] = useState<FeedingLog[]>([]);
  const [todayLogs, setTodayLogs] = useState<FeedingLog[]>([]);
  const [feedingChoices, setFeedingChoices] = useState<Record<string, ChoiceOption[]>>({});

  // UI
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>((searchParams.get('tab') as TabKey) || 'log');
  const [toast, setToast] = useState<ToastState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [dateFilter, setDateFilter] = useState<DateFilter>('today');

  // Form
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);

  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Data Fetching                                                    */
  /* ---------------------------------------------------------------- */

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const pp = await womensHealthService.getActivePostpartum();
      setProfile(pp);

      if (pp) {
        const [logs, today, choices] = await Promise.all([
          womensHealthService.getFeedingLogs(pp.id),
          womensHealthService.getTodayFeedingLogs(pp.id),
          womensHealthService.getFeedingLogChoices(),
        ]);
        setFeedingLogs(Array.isArray(logs) ? logs : []);
        setTodayLogs(Array.isArray(today) ? today : []);
        if (choices) setFeedingChoices(choices);
      }
    } catch (err) {
      console.error('Failed to fetch feeding data:', err);
      showToast('Failed to load feeding data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData, activeProfile]);

  /* ---------------------------------------------------------------- */
  /*  Today's Summary                                                  */
  /* ---------------------------------------------------------------- */

  const todaySummary = useMemo(() => {
    const totalFeeds = todayLogs.length;
    const totalNursingMinutes = todayLogs
      .filter((l) => l.feeding_type === 'nursing')
      .reduce((sum, l) => sum + (l.duration_minutes || 0), 0);
    const totalPumpedMl = todayLogs
      .filter((l) => l.feeding_type === 'pumping' || l.feeding_type === 'bottle')
      .reduce((sum, l) => sum + (l.volume_ml || 0), 0);
    return { totalFeeds, totalNursingMinutes, totalPumpedMl };
  }, [todayLogs]);

  /* ---------------------------------------------------------------- */
  /*  Filtered Logs                                                    */
  /* ---------------------------------------------------------------- */

  const filteredLogs = useMemo(() => {
    if (dateFilter === 'today') return feedingLogs.filter((l) => isToday(l.start_time));
    if (dateFilter === 'week') return feedingLogs.filter((l) => isThisWeek(l.start_time));
    return feedingLogs;
  }, [feedingLogs, dateFilter]);

  /* ---------------------------------------------------------------- */
  /*  CRUD                                                             */
  /* ---------------------------------------------------------------- */

  const handleSave = async () => {
    if (!formData.feeding_type) {
      showToast('Please select a feeding type.', 'error');
      return;
    }
    try {
      setSaving(true);
      const payload: any = {
        postpartum: profile!.id,
        feeding_type: formData.feeding_type,
        side: formData.side || undefined,
        start_time: formData.start_time ? new Date(formData.start_time).toISOString() : new Date().toISOString(),
        notes: formData.notes.trim(),
      };
      if (formData.duration_minutes !== '' && formData.duration_minutes !== undefined) {
        payload.duration_minutes = Number(formData.duration_minutes);
      }
      if (formData.volume_ml !== '' && formData.volume_ml !== undefined) {
        payload.volume_ml = Number(formData.volume_ml);
      }

      if (editingId) {
        await womensHealthService.updateFeedingLog(editingId, payload);
        showToast('Feeding log updated!', 'success');
      } else {
        await womensHealthService.createFeedingLog(payload);
        showToast('Feeding session logged!', 'success');
      }
      setFormData(INITIAL_FORM);
      setEditingId(null);
      await fetchData();
    } catch (err) {
      console.error('Error saving feeding log:', err);
      showToast('Failed to save feeding log.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (log: FeedingLog) => {
    const localTime = log.start_time ? new Date(log.start_time).toISOString().slice(0, 16) : nowLocalDatetime();
    setFormData({
      feeding_type: log.feeding_type,
      side: log.side || '',
      start_time: localTime,
      duration_minutes: log.duration_minutes ?? '',
      volume_ml: log.volume_ml ?? '',
      notes: log.notes || '',
    });
    setEditingId(log.id);
    setActiveTab('log');
  };

  const handleDelete = async () => {
    if (confirmDelete === null) return;
    try {
      await womensHealthService.deleteFeedingLog(confirmDelete);
      showToast('Feeding log deleted.', 'success');
      setConfirmDelete(null);
      await fetchData();
    } catch (err) {
      console.error('Error deleting:', err);
      showToast('Failed to delete entry.', 'error');
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Tabs                                                             */
  /* ---------------------------------------------------------------- */

  const tabs: { key: TabKey; label: string; icon: IconDefinition }[] = [
    { key: 'log', label: t('womensHealth:postpartum.feeding.logFeed'), icon: faPlus },
    { key: 'history', label: t('womensHealth:postpartum.feeding.feedingHistory'), icon: faClock },
    { key: 'solids', label: 'Solid Foods Guide', icon: faAppleWhole },
  ];

  /* ================================================================ */
  /*  Render: No Profile                                               */
  /* ================================================================ */

  const renderNoProfile = () => (
    <div className="wh-feed-no-profile">
      <div className="wh-feed-no-profile-icon">
        <FontAwesomeIcon icon={faBaby} />
      </div>
      <h2>Set Up Your Postpartum Profile First</h2>
      <p>You need a postpartum profile to start logging feeding sessions.</p>
      <a href="/womens-health/postpartum-recovery" className="wh-feed-btn wh-feed-btn-primary">
        <FontAwesomeIcon icon={faArrowRight} /> Go to Postpartum Recovery
      </a>
    </div>
  );

  /* ================================================================ */
  /*  Render: Today's Summary                                          */
  /* ================================================================ */

  const renderTodaySummary = () => (
    <div className="wh-feed-today-row">
      <div className="wh-feed-today-card">
        <div className="wh-feed-today-icon" style={{ background: '#f3e8ff', color: '#a855f7' }}>
          <FontAwesomeIcon icon={faUtensils} />
        </div>
        <div className="wh-feed-today-info">
          <span className="wh-feed-today-value">{todaySummary.totalFeeds}</span>
          <span className="wh-feed-today-label">Total Feeds Today</span>
        </div>
      </div>
      <div className="wh-feed-today-card">
        <div className="wh-feed-today-icon" style={{ background: '#fce7f3', color: '#ec4899' }}>
          <FontAwesomeIcon icon={faClock} />
        </div>
        <div className="wh-feed-today-info">
          <span className="wh-feed-today-value">{todaySummary.totalNursingMinutes} min</span>
          <span className="wh-feed-today-label">Total Nursing</span>
        </div>
      </div>
      <div className="wh-feed-today-card">
        <div className="wh-feed-today-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
          <FontAwesomeIcon icon={faBottleWater} />
        </div>
        <div className="wh-feed-today-info">
          <span className="wh-feed-today-value">{todaySummary.totalPumpedMl} ml</span>
          <span className="wh-feed-today-label">Total Volume</span>
        </div>
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: Log Session Tab                                          */
  /* ================================================================ */

  const renderLogTab = () => (
    <div className="wh-feed-tab-panel">
      <div className="wh-feed-form-card">
        <h3 className="wh-feed-form-card-title">
          <FontAwesomeIcon icon={editingId ? faPen : faPlus} />
          {editingId ? t('common:buttons.edit') : t('womensHealth:postpartum.feeding.logFeed')}
        </h3>
        <div className="wh-feed-form-grid">
          <div className="wh-feed-form-group">
            <label className="wh-feed-form-label">
              Feeding Type <span className="wh-feed-required">*</span>
            </label>
            <select
              className="wh-feed-form-select"
              value={formData.feeding_type}
              onChange={(e) => setFormData({ ...formData, feeding_type: e.target.value })}
            >
              <option value="">Select type...</option>
              {(feedingChoices.feeding_type || []).map((c) => (
                <option key={c.value} value={c.value}>{c.display_name}</option>
              ))}
            </select>
          </div>
          <div className="wh-feed-form-group">
            <label className="wh-feed-form-label">{t('womensHealth:postpartum.feeding.side')}</label>
            <select
              className="wh-feed-form-select"
              value={formData.side}
              onChange={(e) => setFormData({ ...formData, side: e.target.value })}
            >
              <option value="">Select side...</option>
              {(feedingChoices.side || []).map((c) => (
                <option key={c.value} value={c.value}>{c.display_name}</option>
              ))}
            </select>
          </div>
          <div className="wh-feed-form-group">
            <label className="wh-feed-form-label">Start Time</label>
            <input
              type="datetime-local"
              className="wh-feed-form-input"
              value={formData.start_time}
              onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
            />
          </div>
          <div className="wh-feed-form-group">
            <label className="wh-feed-form-label">{t('womensHealth:postpartum.feeding.duration')}</label>
            <input
              type="number"
              className="wh-feed-form-input"
              min={0}
              max={120}
              placeholder="e.g. 20"
              value={formData.duration_minutes}
              onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
            />
          </div>
          <div className="wh-feed-form-group">
            <label className="wh-feed-form-label">{t('womensHealth:postpartum.feeding.amount')}</label>
            <input
              type="number"
              className="wh-feed-form-input"
              min={0}
              max={500}
              placeholder="For pumping/bottle"
              value={formData.volume_ml}
              onChange={(e) => setFormData({ ...formData, volume_ml: e.target.value })}
            />
          </div>
        </div>
        <div className="wh-feed-form-group" style={{ marginTop: 12 }}>
          <label className="wh-feed-form-label">Notes</label>
          <textarea
            className="wh-feed-form-textarea"
            rows={3}
            placeholder="Any observations about this feeding session..."
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
        </div>
        <div className="wh-feed-form-actions">
          {editingId && (
            <button
              className="wh-feed-btn wh-feed-btn-cancel"
              onClick={() => {
                setFormData(INITIAL_FORM);
                setEditingId(null);
              }}
            >
              {t('common:buttons.cancel')}
            </button>
          )}
          <button
            className="wh-feed-btn wh-feed-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> {t('common:buttons.save')}...</>
            ) : (
              <><FontAwesomeIcon icon={faCheck} /> {editingId ? t('common:buttons.update') : t('womensHealth:postpartum.feeding.logFeed')}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: History Tab                                              */
  /* ================================================================ */

  const renderHistoryTab = () => (
    <div className="wh-feed-tab-panel">
      {/* Date Filters */}
      <div className="wh-feed-filter-row">
        <FontAwesomeIcon icon={faFilter} className="wh-feed-filter-icon" />
        {(['today', 'week', 'all'] as DateFilter[]).map((f) => (
          <button
            key={f}
            className={`wh-feed-filter-btn ${dateFilter === f ? 'active' : ''}`}
            onClick={() => setDateFilter(f)}
          >
            {f === 'today' ? 'Today' : f === 'week' ? 'This Week' : 'All'}
          </button>
        ))}
      </div>

      {loading ? (
        <SkeletonCards />
      ) : filteredLogs.length === 0 ? (
        <div className="wh-feed-empty-state">
          <div className="wh-feed-empty-icon-wrap">
            <FontAwesomeIcon icon={faUtensils} />
          </div>
          <h3>No feeding logs {dateFilter !== 'all' ? `for ${dateFilter === 'today' ? 'today' : 'this week'}` : 'yet'}</h3>
          <p>Start logging feeding sessions to track your baby's nutrition.</p>
          <button
            className="wh-feed-btn wh-feed-btn-primary"
            onClick={() => setActiveTab('log')}
          >
            <FontAwesomeIcon icon={faPlus} /> Log Session
          </button>
        </div>
      ) : (
        <div className="wh-feed-history-grid">
          {filteredLogs.map((log) => (
            <div key={log.id} className="wh-feed-history-card">
              <div className="wh-feed-history-header">
                <span className="wh-feed-history-time">
                  <FontAwesomeIcon icon={faClock} /> {formatDateTime(log.start_time)}
                </span>
              </div>
              <div className="wh-feed-badges-row">
                <span className="wh-feed-badge wh-feed-badge-type">
                  <FontAwesomeIcon icon={faUtensils} /> {log.feeding_type_display || log.feeding_type}
                </span>
                {log.side && (
                  <span className="wh-feed-badge wh-feed-badge-side">
                    {log.side_display || log.side}
                  </span>
                )}
                {log.duration_minutes != null && log.duration_minutes > 0 && (
                  <span className="wh-feed-badge wh-feed-badge-duration">
                    <FontAwesomeIcon icon={faClock} /> {log.duration_minutes} min
                  </span>
                )}
                {log.volume_ml != null && log.volume_ml > 0 && (
                  <span className="wh-feed-badge wh-feed-badge-volume">
                    <FontAwesomeIcon icon={faDroplet} /> {log.volume_ml} ml
                  </span>
                )}
              </div>
              {log.notes && (
                <p className="wh-feed-history-notes">
                  <FontAwesomeIcon icon={faNotesMedical} /> {log.notes}
                </p>
              )}
              <div className="wh-feed-history-actions">
                <button className="wh-feed-action-btn wh-feed-action-edit" onClick={() => handleEdit(log)}>
                  <FontAwesomeIcon icon={faPen} /> {t('common:buttons.edit')}
                </button>
                <button className="wh-feed-action-btn wh-feed-action-delete" onClick={() => setConfirmDelete(log.id)}>
                  <FontAwesomeIcon icon={faTrash} /> {t('common:buttons.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /* ================================================================ */
  /*  Render: Solid Foods Guide Tab                                    */
  /* ================================================================ */

  const renderSolidsTab = () => (
    <div className="wh-feed-tab-panel">
      <div className="wh-feed-guide-intro">
        <h3>Solid Food Introduction Timeline</h3>
        <p>
          Every baby is different, but this general timeline can help guide you through introducing solid foods.
          Always consult your pediatrician before starting solids and watch for signs of readiness such as sitting
          with support, showing interest in food, and being able to move food to the back of the mouth.
        </p>
      </div>

      <WHArticleList
        stage="postpartum"
        contentType="solid_foods"
        variant="accordion"
        icon={faAppleWhole}
        accentColor="#22c55e"
      />
    </div>
  );

  /* ================================================================ */
  /*  Main Render                                                      */
  /* ================================================================ */

  return (
    <div className="wh-feed-page">
      {/* Toast */}
      {toast && (
        <div className="wh-feed-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      {/* Delete Modal */}
      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Feeding Log?"
        message="This action cannot be undone."
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(null)}
      />

      {/* Hero Banner */}
      <section className="wh-feed-hero">
        <div className="wh-feed-hero-circle wh-feed-hero-circle-1" />
        <div className="wh-feed-hero-circle wh-feed-hero-circle-2" />
        <Container>
          <BackButton />
          <div className="wh-feed-hero-content">
            <div className="wh-feed-hero-badge">
              <FontAwesomeIcon icon={faUtensils} />
              Postpartum
            </div>
            <h1 className="wh-feed-hero-title">{t('womensHealth:postpartum.feeding.title')}</h1>
            <p className="wh-feed-hero-subtitle">
              Track breastfeeding sessions, pumping volumes, and follow an age-appropriate solid food introduction guide.
            </p>
          </div>
        </Container>
      </section>

      {/* Today's Summary */}
      {profile && (
        <section className="wh-feed-summary-section">
          <Container>
            {renderTodaySummary()}
          </Container>
        </section>
      )}

      {/* Content */}
      <Container>
        {loading ? (
          <SkeletonCards />
        ) : !profile ? (
          renderNoProfile()
        ) : (
          <>
            <ProfileStrip
              summary={`Delivery ${formatDate(profile.delivery_date)} · ${profile.recovery_mode_display || profile.recovery_mode || 'Recovery'}`}
              hubPath="/womens-health/postpartum-recovery"
              hubLabel="Recovery Hub"
            />
            <div className="wh-feed-tabs-container">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  className={`wh-feed-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <FontAwesomeIcon icon={tab.icon} />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="wh-feed-tab-content">
              {activeTab === 'log' && renderLogTab()}
              {activeTab === 'history' && renderHistoryTab()}
              {activeTab === 'solids' && renderSolidsTab()}
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default Feeding;
